import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiMenu, FiX, FiStar} = FiIcons;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const navItems = [
    {name: 'เส้นทาง', href: 'segmentation'},
    {name: 'หลักสูตร', href: 'curriculum'},
    {name: 'ผู้สอน', href: 'instructor'},
    {name: 'การลงทุน', href: 'investment'},
    {name: 'FAQ', href: 'faq'}
  ];

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsOpen(false); // Close mobile menu
  };

  // Handle scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => item.href);
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.mobile-menu-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <motion.nav
      className="fixed top-0 w-full z-50 bg-black/10 backdrop-blur-xl border-b border-white/5"
      initial={{y: -100}}
      animate={{y: 0}}
      transition={{duration: 0.6}}
    >
      <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 max-w-none">
        <div className="flex justify-between items-center h-14 sm:h-16 lg:h-20">
          {/* Logo */}
          <motion.div
            className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3 cursor-pointer flex-shrink-0 min-w-0"
            whileHover={{scale: 1.05}}
            onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiStar} className="text-white text-xs sm:text-sm lg:text-lg" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-white font-light text-sm sm:text-base lg:text-lg xl:text-xl truncate">Bazi Blueprint</div>
              <div className="text-indigo-300 text-xs font-light hidden sm:block">ศาสตร์แห่งปาจื้อ</div>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6 2xl:space-x-8">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className={`text-white/70 hover:text-white transition-colors duration-200 font-light text-sm xl:text-base whitespace-nowrap ${
                  activeSection === item.href ? 'text-white border-b border-indigo-400' : ''
                }`}
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
              >
                {item.name}
              </motion.button>
            ))}
            <motion.a
              href="https://www.fengshuix.com/event-details/the-bazi-blueprint"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 xl:px-4 2xl:px-6 py-2 xl:py-3 rounded-lg xl:rounded-xl font-light text-sm xl:text-base hover:shadow-lg transition-all duration-200 whitespace-nowrap"
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
            >
              เริ่มการเดินทาง
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden mobile-menu-container flex-shrink-0">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-1 sm:p-2 relative z-60 flex-shrink-0"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden z-40"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* Mobile Menu */}
        {isOpen && (
          <motion.div
            className="fixed top-14 sm:top-16 left-2 right-2 sm:left-4 sm:right-4 bg-slate-900/95 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:hidden z-50 mobile-menu-container border border-white/10 max-w-xs sm:max-w-sm mx-auto"
            initial={{opacity: 0, y: -20, scale: 0.95}}
            animate={{opacity: 1, y: 0, scale: 1}}
            exit={{opacity: 0, y: -20, scale: 0.95}}
            transition={{duration: 0.3}}
          >
            <div className="space-y-1 sm:space-y-2">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className={`block w-full text-left text-white/70 hover:text-white py-2 sm:py-3 px-2 sm:px-3 rounded-lg transition-all duration-200 font-light text-sm ${
                    activeSection === item.href ? 'bg-white/10 text-white' : 'hover:bg-white/5'
                  }`}
                  initial={{opacity: 0, x: -20}}
                  animate={{opacity: 1, x: 0}}
                  transition={{delay: index * 0.1}}
                >
                  {item.name}
                </motion.button>
              ))}
              
              <motion.a
                href="https://www.fengshuix.com/event-details/the-bazi-blueprint"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 sm:py-4 rounded-lg font-light mt-3 sm:mt-4 text-center text-sm"
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.3}}
              >
                เริ่มการเดินทาง
              </motion.a>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navigation;